<script lang="ts" module>
  import type { HTMLInputAttributes } from 'svelte/elements';
  import { clsx } from 'clsx';
  import { tv } from 'tailwind-variants';

  export interface InputButtonProps<TValue>
    extends Omit<HTMLInputAttributes, 'onchange' | 'checked'> {
    label: string;
    value: TValue;
    checked?: boolean;
    onchange?: (checked: boolean, value: TValue) => void;
    disabled?: boolean;
  }

  const styles = tv({
    slots: {
      base: 'border-gradient fr relative rounded-md border from-border-gradient-start to-border-gradient-end px-4 py-3 text-lsm hocus:to-border-gradient-end-H',
      icon: 'size-4 text-c-success transition-all duration-500 ease-in-out',
    },
    variants: {
      checked: {
        true: { base: 'font-medium', icon: 'ml-2 opacity-100' },
        false: {
          base: '',
          icon: 'relative w-0 opacity-0',
        },
      },
      disabled: {
        true: { base: 'opacity-50' },
        false: {},
      },
    },
  });
</script>

<script lang="ts" generics="TValue">
  import { Icon } from '@/ui/base';

  let {
    label,
    checked,
    disabled,
    onchange,
    value,
    class: className,
    ...rest
  }: InputButtonProps<TValue> = $props();
  let { base: baseCn, icon: iconCn } = $derived(
    styles({ checked: checked ?? undefined, disabled, className: clsx(className) }),
  );
</script>

<label class={baseCn()}>
  <span class="select-none text-lsm">{label}</span>
  <input
    type="radio"
    {...rest}
    onchange={(evt) => onchange?.(evt.currentTarget?.checked, value)}
    {checked}
    class="absolute right-4 -z-10 size-10 opacity-0"
    {disabled}
  />

  <Icon icon="CheckCircleOutline" class={iconCn()} role="presentation" />
</label>
